import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserLicensesQuery } from "@/graphql/queries/UserLicenses";
import { useFeatureFlags } from "@/providers/FeatureFlagProvider/FeatureFlagProvider";
import { useAuthenticationState } from "../useAuthenticationState/useAuthenticationState";
import { useQueryWrapper } from "../useQueryWrapper/useQueryWrapper";

type LicenseName =
  | "ai_assist_pro"
  | "analytics_plus"
  | "aircall_ai"
  | "whatsapp_messaging"
  | "salesforce_service_cloud_voice";

export function useGetLicenses() {
  const { t } = useTranslation();
  const {
    authState: { decodedToken },
  } = useAuthenticationState();

  const { conversationIntelligenceCustomerProvisioning, advancedAiPackage } =
    useFeatureFlags();

  const skipQuery =
    !conversationIntelligenceCustomerProvisioning ||
    !decodedToken?.["custom:user_id"];

  const { loading, data } = useQueryWrapper(UserLicensesQuery, {
    variables: {
      userId: decodedToken?.["custom:user_id"] ?? "",
    },
    skip: skipQuery,
    showErrorToast: () => t("toast.fetch_failure"),
  });

  const licensesRes = useMemo(() => {
    if (skipQuery) {
      return [
        {
          name: "aircall_ai",
          active: advancedAiPackage,
        },
      ];
    }

    return data?.userLicenses?.licenses ?? [];
  }, [data, skipQuery, advancedAiPackage]);

  const hasLicense = useCallback(
    (licenseName: LicenseName) => {
      return licensesRes.some(
        (license) => license.name === licenseName && license.active
      );
    },
    [licensesRes]
  );

  return {
    loading,
    licenses: licensesRes,
    hasLicense,
  };
}
